import React from 'react';
import dayjs from 'dayjs';
import type { CertificateOfConformityRecord } from '@/services/certificate-of-conformity.service';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';

export type CertificateOfConformityPrintCompanyInfo = {
  name: string;
  legalName?: string;
  address: string;
  logo?: string;
};

export type CertificateOfConformityPrintViewProps = {
  record: CertificateOfConformityRecord;
  companyInfo: CertificateOfConformityPrintCompanyInfo;
};

function dash(value?: string | null): string {
  if (value == null || String(value).trim() === '') return '—';
  return String(value);
}

function formatTopDate(value?: string | null): string {
  if (!value) return '';
  return dayjs(value).format('DD MMM YYYY').toUpperCase();
}

function formatBodyDate(value?: string | null): string {
  if (!value) return '';
  return dayjs(value).format('DD. MMM YYYY').toUpperCase();
}

function formatQuantity(value?: number | null): string {
  if (value == null || Number.isNaN(Number(value))) return '';
  return `${Number(value).toLocaleString()} No`;
}

/** Certificate of conformity — shared pagination engine with restored CoC fields. */
export function CertificateOfConformityPrintView({
  record,
  companyInfo,
}: CertificateOfConformityPrintViewProps) {
  const documentRevision = dash(record.document_revision);
  const poAndJobNumber = [record.po_number, record.job_number]
    .filter((item) => String(item || '').trim())
    .join(' / ');
  const legal = companyInfo.legalName || companyInfo.name;

  return (
    <EngineCommercialA4Print
      docType="certificate_of_conformity"
      title="Certificate of Conformity"
      documentNumber={record.document_number || 'COC'}
      partyName={dash(record.job_card_number)}
      introText={`${companyInfo.name} · DATE: ${formatTopDate(record.date) || '—'}`}
      metaLines={[
        { label: 'Document Number', value: dash(record.document_number) },
        { label: 'REV', value: documentRevision },
        { label: 'Job Card Number', value: dash(record.job_card_number) },
        { label: 'PO Number / Job Number', value: poAndJobNumber || '' },
        { label: 'Supplier PO Number', value: dash(record.supplier_po_number) },
        { label: 'Heat Number', value: dash(record.heat_number) },
        { label: 'Part Number', value: dash(record.part_number) },
        { label: 'Part Description', value: dash(record.part_description) },
        { label: 'Part Revision Level', value: dash(record.part_revision_level) },
        { label: 'Date of Completion', value: formatBodyDate(record.date_of_completion) },
        { label: 'Quantity', value: formatQuantity(record.quantity) },
      ].filter((m) => m.value && m.value !== '—')}
      columns={[
        { key: 'sno', label: '#' },
        { key: 'description', label: 'Certified detail' },
        { key: 'value', label: 'Value' },
      ]}
      lines={[
        {
          lineNumber: 1,
          description: 'Part',
          cells: {
            value: `${dash(record.part_number)} — ${dash(record.part_description)}`,
          },
        },
        {
          lineNumber: 2,
          description: 'Quantity',
          cells: { value: formatQuantity(record.quantity) || '—' },
        },
        {
          lineNumber: 3,
          description: 'Heat Number',
          cells: { value: dash(record.heat_number) },
        },
      ]}
      finals={[
        ...(record.notes ? [{ id: 'notes', heightPx: 48 }] : []),
        { id: 'declaration', heightPx: 72 },
        { id: 'sign', heightPx: 56 },
      ]}
      finalBlocks={{
        notes: record.notes ? (
          <div>
            <strong>Notes:</strong>
            <p>{dash(record.notes)}</p>
          </div>
        ) : null,
        declaration: (
          <p>
            This is to certify that above mentioned detailed job is manufactured by {legal} and it
            checked as per given specification of part Number (Drawing) and has satisfactorily passed
            our Quality Control Procedures.
          </p>
        ),
        sign: (
          <div>
            <p>
              <strong>Q/C Engineer</strong>
              <br />
              {dash(record.qc_engineer)}
            </p>
            <p style={{ fontSize: 11, marginTop: 8 }}>
              {legal}
              <br />
              {companyInfo.address}
              <br />
              NTES-QSF-QA/QC-F06
            </p>
          </div>
        ),
      }}
    />
  );
}

export default CertificateOfConformityPrintView;
